import type { VaultMeta } from '@/lib/crypto/vault';
import type { PersonaId } from './personas';

/** History persistence mode. */
export type HistoryMode = 'local' | 'ephemeral' | 'sync';

export interface WorkspaceSettings {
  historyMode: HistoryMode;
  /**
   * Encrypt sensitive fields at rest when the vault is unlocked.
   * Honored by seal policy (`setSealEnabled` / `shouldSeal`).
   * Default true when a vault exists.
   */
  encryptLocal: boolean;
  /** Vault key wrap metadata (no raw key material) */
  vault?: VaultMeta;
  /**
   * Global system instructions — injected into every chat (freestanding and project).
   * Project chats stack: global → project instructions → memory → context → thread override.
   * Sealed at rest when the vault is active.
   */
  globalInstructions?: string;
  /** Last active project id (undefined when in a freestanding chat) */
  activeProjectId?: string;
  /** Last active chat id (freestanding or project) */
  activeChatId?: string;
  /** Last active chat id per project */
  activeChatByProject: Record<string, string>;
  /** Global default model fullId (provider/model) */
  defaultModelId?: string;
  /** Last successful encrypted sync publish (ms) */
  syncLastPublishedAt?: number;
  /** Last successful encrypted sync pull (ms) */
  syncLastPulledAt?: number;
  /**
   * Auto-lock vault after this many minutes of inactivity (0 = off).
   * Protects against unattended unlocked sessions. Default 30.
   */
  idleLockMinutes?: number;
  /**
   * When true, sync snapshots omit message bodies (projects/settings/chats only).
   * Smaller, less sensitive relay payloads.
   */
  syncExcludeMessages?: boolean;
  /**
   * Origin+pubkey fingerprint that already completed auto-pull bootstrap.
   * Prevents surprise re-pull after intentional empty workspace.
   */
  syncAutoPullDoneKey?: string;
  /**
   * User acknowledged CORS proxy MITM risk (required before enabling proxy).
   */
  corsProxyRiskAcknowledged?: boolean;
  /**
   * Nostr pubkey (hex) that owns this browser workspace.
   * Set on first write after login. Mismatch with current login blocks the UI
   * until the user switches account or clears local data (account isolation).
   */
  ownerPubkey?: string;
  /**
   * Include logged-in Nostr identity in the system session block sent to models.
   * Default true when logged in. Off = model does not receive npub/profile from us.
   */
  includeNostrIdentity?: boolean;
  /**
   * How much identity detail to inject when includeNostrIdentity is on.
   * - minimal: npub + hex only
   * - standard: + name, nip05, about snippet (default)
   * - full: + relay list summary when available
   */
  nostrIdentityDetail?: 'minimal' | 'standard' | 'full';
  /**
   * When the user @-mentions someone (or uses My recent notes packs),
   * auto-fetch recent public notes. Default true.
   */
  mentionAutoFetchNotes?: boolean;
  /**
   * Default chat persona for new turns (overridable per chat).
   * See `lib/workspace/personas.ts`.
   */
  defaultPersonaId?: PersonaId | string;
  /**
   * When true, inject a grounded-only rule: answer from project brain +
   * attached/fetched context; refuse to invent outside that. Default false.
   */
  groundedOnly?: boolean;
}

/** Kind of Nostr entity stored in a context item or composer chip. */
export type NostrContextEntity =
  | 'event'
  | 'profile'
  | 'addr'
  | 'thread'
  | 'pack';

/** Recipe used when expanding a Nostr context item / mention. */
export type NostrContextPack =
  | 'single'
  | 'thread'
  | 'profile-brief'
  | 'profile-recent'
  | 'article'
  | 'my-profile'
  | 'my-recent'
  /**
   * Search / recent scan across the logged-in user's follow graph.
   * Query text lives in chip.snippet (optional → recent feed).
   */
  | 'network-search';

/** Optional metadata on nostr-type context items (and composer chips). */
export interface NostrContextMeta {
  /** Canonical bech32 or hex ref */
  ref: string;
  entity: NostrContextEntity;
  authorPubkey?: string;
  eventKind?: number;
  /** Display name when known */
  displayName?: string;
  /** Short handle / username */
  name?: string;
  nip05?: string;
  /**
   * Result of forward-resolving `nip05` and comparing to authorPubkey.
   * undefined = not checked yet.
   */
  nip05Verified?: boolean;
  /** Profile avatar */
  picture?: string;
  /** Article/event cover or profile banner */
  image?: string;
  /** Kind-0 about blurb or article summary */
  about?: string;
  /** One-line preview of note/article body */
  snippet?: string;
  /** Last successful fetch snapshot (optional offline fallback) */
  cachedText?: string;
  cachedAt?: number;
  relatedRefs?: string[];
  pack?: NostrContextPack;
  /** Notes to pull for profile-recent packs */
  noteLimit?: number;
}

/**
 * Resolved @-mention of a Nostr person (composer / message).
 * Canonical pubkey drives fetch; displayName is for the bubble.
 */
export interface ChatMention {
  id: string;
  pubkey: string;
  npub: string;
  displayName: string;
  nip05?: string;
  /** Forward-resolution check result for nip05 (undefined = unchecked). */
  nip05Verified?: boolean;
  picture?: string;
  /**
   * When true (default), expand profile + recent notes on send.
   * When false, only inject a short identity line.
   */
  fetchNotes?: boolean;
}

/**
 * Ephemeral / attached Nostr context chip on a message or composer
 * (parallel to file attachments — not a file).
 */
export interface NostrContextChip {
  id: string;
  title: string;
  /** Primary ref (npub, nevent, …) */
  ref: string;
  pack: NostrContextPack;
  pubkey?: string;
  eventId?: string;
  nip05?: string;
  /** Forward-resolution check result for nip05 (undefined = unchecked). */
  nip05Verified?: boolean;
  /** Profile avatar URL (kind-0 picture) */
  picture?: string;
  /** Article/event cover or banner (NIP-23 image tag, etc.) */
  image?: string;
  snippet?: string;
  /** Optional pre-fetched body (set after resolve preview) */
  cachedText?: string;
  noteLimit?: number;
  /**
   * When true, this chip was created from an @mention (UI parity with + packs).
   * Still expands on send like any other chip.
   */
  fromMention?: boolean;
}

export interface ContextItem {
  id: string;
  type: 'note' | 'url' | 'nostr';
  title?: string;
  /** Body text, URL, or nostr bech32/hex ref */
  content: string;
  pinned?: boolean;
  createdAt: number;
  /** Structured Nostr metadata when type === 'nostr' */
  meta?: NostrContextMeta;
}

/**
 * Durable facts the model should remember across chats in a project.
 * Injected into the system prompt (after instructions, before context pack).
 * Private to this browser/workspace — never shared automatically.
 */
export interface MemoryItem {
  id: string;
  content: string;
  /** Optional short label */
  title?: string;
  createdAt: number;
  updatedAt: number;
  /** How the memory was created */
  source?: 'manual' | 'chat';
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  /** Always injected as system context */
  instructions: string;
  /**
   * Project memory — short facts recalled in every chat.
   * Older projects may omit this; treat missing as [].
   */
  memoryItems?: MemoryItem[];
  contextItems: ContextItem[];
  /** Default model fullId for new chats in this project */
  defaultModelId?: string;
  createdAt: number;
  updatedAt: number;
  archived?: boolean;
}

export interface Chat {
  id: string;
  /**
   * Owning project, or null/undefined for freestanding chats
   * (Claude-style recents outside any project).
   */
  projectId?: string | null;
  title: string;
  /** Optional per-chat system override (appended after project instructions) */
  instructionOverride?: string;
  /** Optional model override for this chat */
  modelOverride?: string;
  /**
   * Per-chat persona override. When unset, workspace defaultPersonaId applies.
   */
  personaId?: PersonaId | string;
  /**
   * Per-chat grounded-only override. When undefined, use workspace groundedOnly.
   */
  groundedOnly?: boolean;
  /**
   * Prompt layers / items excluded from injection for this chat.
   * Layer keys: session | global | persona | grounded | project_instr |
   * memory | context | thread | nostr_chips | mentions | attachments
   * Item keys: memory:{id} | context:{id}
   * Ephemeral composer layers (nostr/mentions/attachments) may also be
   * held only in UI state for the current turn.
   */
  promptOmit?: string[];
  createdAt: number;
  updatedAt: number;
}

/** Known layer-level omit keys (item keys use memory: / context: prefixes). */
export type PromptOmitLayer =
  | 'session'
  | 'global'
  | 'persona'
  | 'grounded'
  | 'project_instr'
  | 'memory'
  | 'context'
  | 'thread'
  | 'nostr_chips'
  | 'mentions'
  | 'attachments';

/** True when the chat is not attached to a project. */
export function isFreestandingChat(chat: Pick<Chat, 'projectId'>): boolean {
  return chat.projectId == null || chat.projectId === '';
}

export type MessageRole = 'user' | 'assistant' | 'system';

/** File/image attachment on a user message (data URLs for images; text for files). */
export interface MessageAttachment {
  id: string;
  /** MIME type, e.g. image/png, text/plain, application/pdf */
  mimeType: string;
  /** Original filename when known */
  name?: string;
  /**
   * For images: data URL (or https URL).
   * For text-like files: UTF-8 body (truncated if huge).
   * For binary we can't inline: empty + note in name.
   */
  data: string;
  /** Byte size before encoding (approx) */
  size?: number;
  kind: 'image' | 'text' | 'file';
}

/**
 * What was injected into the system stack for an assistant turn.
 * Stored on the assistant message so the UI can show “Used: …” receipts.
 * Labels only — no full bodies (those stay in project / history).
 */
export type ReceiptKind =
  | 'session'
  | 'global'
  | 'persona'
  | 'grounded'
  | 'project_instructions'
  | 'memory'
  | 'context'
  | 'thread'
  | 'nostr_pack'
  | 'nostr_mention'
  | 'nostr_auto'
  | 'attachment';

export interface MessageReceipt {
  kind: ReceiptKind;
  /** Short human label */
  label: string;
  /** Optional detail (memory title, context item title, pack name) */
  detail?: string;
  /** Count when aggregated (e.g. 3 memories) */
  count?: number;
}

export interface Message {
  id: string;
  chatId: string;
  role: MessageRole;
  content: string;
  createdAt: number;
  /** Model used for assistant turns */
  modelId?: string;
  /** Optional attachments (user turns; sealed with content when vault is on) */
  attachments?: MessageAttachment[];
  /** @-mentions resolved in this user turn */
  mentions?: ChatMention[];
  /** Explicit Nostr context chips attached to this user turn */
  nostrContext?: NostrContextChip[];
  /**
   * Layers / sources injected for this assistant turn (receipts).
   * Only set on assistant messages after a completed generation.
   */
  receipts?: MessageReceipt[];
}

/**
 * Public AI provider config for export/sync (never includes API keys).
 * On a new device the user re-enters keys; base URL + models restore.
 */
export interface PortableAIProvider {
  id: string;
  name: string;
  baseUrl: string;
  useCorsProxy: boolean;
  models: { id: string; name: string; providerId: string }[];
  modelsFetchedAt?: number;
}

/** Portable export format (plain JSON). */
export interface WorkspaceExport {
  version: 1;
  exportedAt: number;
  app: 'opendork';
  format?: 'plain';
  settings?: Partial<WorkspaceSettings>;
  projects: Project[];
  chats: Chat[];
  messages: Message[];
  /** BYOK providers without secrets (optional; older backups omit). */
  providers?: PortableAIProvider[];
  defaultAiProvider?: string;
}

/** Passphrase-encrypted export bundle. */
export interface EncryptedWorkspaceExport {
  version: 1;
  exportedAt: number;
  app: 'opendork';
  format: 'encrypted';
  /** AES-GCM bundle of JSON WorkspaceExport */
  salt: string;
  iv: string;
  ct: string;
}

export const DEFAULT_WORKSPACE_SETTINGS: WorkspaceSettings = {
  historyMode: 'local',
  encryptLocal: true,
  activeChatByProject: {},
  globalInstructions: '',
  idleLockMinutes: 30,
  syncExcludeMessages: false,
  includeNostrIdentity: true,
  nostrIdentityDetail: 'standard',
  mentionAutoFetchNotes: true,
  defaultPersonaId: 'default',
  groundedOnly: false,
};

/** Soft limits for import / sync payloads (DoS + accidental huge dumps). */
export const WORKSPACE_LIMITS = {
  maxProjects: 500,
  maxChats: 5_000,
  maxMessages: 100_000,
  maxProviders: 50,
  maxJsonBytes: 25 * 1024 * 1024, // 25 MB
  maxStringField: 2_000_000,
  warnPublishBytes: 2 * 1024 * 1024, // 2 MB
  warnPublishMessages: 2_000,
} as const;

/**
 * Built-in baseline when the user has not set global instructions.
 * Always the outermost layer of the system prompt.
 */
export const DEFAULT_GLOBAL_INSTRUCTIONS =
  'You are Dork in OpenDork, a Nostr-native browser AI workspace. Be concise, helpful, and direct. Use markdown when useful. Prefer the OpenDork session block and any Fetched Nostr content over inventing tooling or asking for secrets.';

/** @deprecated Prefer DEFAULT_GLOBAL_INSTRUCTIONS — kept for older imports. */
export const DEFAULT_STANDALONE_INSTRUCTIONS = DEFAULT_GLOBAL_INSTRUCTIONS;

/**
 * Default for new projects is empty: project instructions are additive on top
 * of global instructions. Users fill these in on the project hub.
 */
export const DEFAULT_PROJECT_INSTRUCTIONS = '';

/**
 * Legacy project default strings (pre global-instructions).
 * Matched exactly on load and treated as empty so global is not duplicated.
 */
export const LEGACY_DEFAULT_PROJECT_INSTRUCTIONS = [
  'You are Dork, an AI assistant in OpenDork — a minimal terminal-style interface. Be concise, helpful, and direct. Use markdown for formatting when helpful.',
  'You are Dork, an AI assistant in OpenDork. Be concise, helpful, and direct. Use markdown for formatting when helpful.',
  DEFAULT_GLOBAL_INSTRUCTIONS,
] as const;

export function isLegacyDefaultProjectInstructions(text: string | undefined | null): boolean {
  if (text == null) return true;
  const t = text.trim();
  if (!t) return true;
  return (LEGACY_DEFAULT_PROJECT_INSTRUCTIONS as readonly string[]).includes(t);
}

// ─── Nostr context freshness ───

/**
 * Pack recipes whose content is time-sensitive ("recent notes", network
 * search). Cached snapshots of these go stale; static entities (an event,
 * an article, a profile's kind-0) barely change and can be cached longer.
 */
export const LIVE_CONTEXT_PACKS: readonly NostrContextPack[] = [
  'my-recent',
  'profile-recent',
  'network-search',
];

export function isLiveContextPack(pack?: NostrContextPack | null): boolean {
  return !!pack && (LIVE_CONTEXT_PACKS as readonly string[]).includes(pack);
}

/**
 * How long a live pack's cachedText is considered fresh enough to inline
 * into prompts without a relay round-trip. Older snapshots are re-expanded
 * live at send time (cache remains the offline/failure fallback).
 */
export const CONTEXT_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h

/** True when a live pack's snapshot is missing or older than the TTL. */
export function isContextCacheStale(
  meta: Pick<NostrContextMeta, 'pack' | 'cachedAt' | 'cachedText'> | undefined,
  now = Date.now(),
): boolean {
  if (!meta || !isLiveContextPack(meta.pack)) return false;
  if (!meta.cachedText?.trim()) return false; // no cache at all — handled as live fetch
  if (!meta.cachedAt) return true;
  return now - meta.cachedAt > CONTEXT_CACHE_TTL_MS;
}
